/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-08 10:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import { Card, Heading, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { showChatbox } from 'src/integrations/chatbox';
import SendSupportTicket from './sendSupportTicket';
import ScheduleDemo from 'src/components/views/common/scheduleDemo';
import { URL } from 'src/constants/links';
import chatIcon from 'src/assets/images/dashboard-v3/get-support/chat-icon.png';
import emailIcon from 'src/assets/images/dashboard-v3/get-support/email-icon.png';
import noteIcon from 'src/assets/images/dashboard-v3/get-support/note-icon.png';
import phoneIcon from 'src/assets/images/dashboard-v3/get-support/phone-icon.png';
import questionIcon from 'src/assets/images/dashboard-v3/get-support/question-icon.png';
import watchIcon from 'src/assets/images/dashboard-v3/get-support/watch-icon.png';
import sendIcon from 'src/assets/images/dashboard-v3/get-support/send-icon.png';
import TypeFormEmbededFeedback from 'src/components/views/common/typeform-embedded-feedback';

import { setWithExpiry, getWithExpiry, getShop } from 'src/helpers';
import { getCorrectPlan } from 'src/helpers/subscription-v3-helper';
import * as themeSetupAction from 'actions/theme-setup-actions';
import * as OauthAction from 'actions/oauth-actions';

type ComposedProps = Props & WithI18nProps;

type State = {
  activeSendRequestModal: boolean,
  activeScheduleDemoModal: boolean,
  actionSurvey: boolean,
  width: number
};

const KEY_ONCE_PER_DAY = 'whatsApp-once-per-day';
const KEY_THREE_TIMES_PER_DAY = 'whatsApp-three-times-per-day';
const KEY_COUNT_CLOSE_WHATSAPP = 'whatsApp-close';
const WHATSAPP_MAXIMUM_APPEARANCE = 3;
const TIME_LOCK_SEND_SUPPORT_TICKET = 60 * 60 * 24 * 1000; // 24h
const TIME_LOCK_AUTO_SHOW = 60 * 60 * 24 * 3 * 1000; // 72h
const MIN_TOTAL_VALUE_OF_PLANS = 990;
const MIN_PLAN_PRICE = 900;

class GetSupport extends Component<ComposedProps, State> {
  state = {
    activeSendRequestModal: false,
    activeScheduleDemoModal: false,
    actionSurvey: false,
    width: window.innerWidth,
    whatsAppShow: false
  };

  componentDidMount() {
    this.props.getTenantAppGroups();
    this.shop = getShop();

    window.addEventListener('resize', this.resizeWindowEvent);

    this.isAlreadySetToday = !!getWithExpiry(`${this.shop}-${KEY_ONCE_PER_DAY}`);
    this.isAlreadyAccessToday = !!getWithExpiry(`${this.shop}-${KEY_THREE_TIMES_PER_DAY}`);

    try {
      this.whatsAppAppearanceCount = localStorage.getItem(
        `${this.shop}-${KEY_COUNT_CLOSE_WHATSAPP}`
      )
        ? JSON.parse(localStorage.getItem(`${this.shop}-${KEY_COUNT_CLOSE_WHATSAPP}`))
        : 0;
    } catch (error) {}

    this.checkAutoShowWhatsApp(this.props);
  }

  componentWillReceiveProps(nextProps) {
    const { tenantAppGroups, chargeInfo } = this.props;
    const { tenantAppGroups: nextTenantAppGroups, chargeInfo: nextChargeInfo } = nextProps;
    if (tenantAppGroups !== nextTenantAppGroups || chargeInfo !== nextChargeInfo) {
      this.checkAutoShowWhatsApp(nextProps);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeWindowEvent);
  }

  resizeWindowEvent = () => {
    this.setState({ width: window.innerWidth });
  };

  checkAutoShowWhatsApp = props => {
    if (
      this.whatsAppAppearanceCount < WHATSAPP_MAXIMUM_APPEARANCE &&
      this.isAdaptSubscriptionPlanWhatsApp(props) &&
      !this.isAlreadyAccessToday
    ) {
      this.autoShowWhatsApp = true;
      this.setState({
        whatsAppShow: true
      });
    }
  };

  isAdaptSubscriptionPlanWhatsApp = props => {
    const { isAdaptMinTotalValue, correctPlan, originalPlan } = props;
    // We will skip this V1 plan because the mapping price to V3 plan is not correct (all V1 plan price are < $900)
    const isOldPlan = originalPlan?.includes('plan');
    return (
      (correctPlan?.price >= MIN_PLAN_PRICE && !!originalPlan && !isOldPlan) || isAdaptMinTotalValue
    );
  };

  openWhatsApp = () => {
    window.open(
      'https://api.whatsapp.com/message/Z3VN5CQZPYIZO1?autoload=1&app_absent=0',
      '_blank'
    );
  };

  handleShowWhatsApp = () => {
    if (this.whatsAppAppearanceCount >= WHATSAPP_MAXIMUM_APPEARANCE || this.isAlreadySetToday) {
      return;
    }

    if (!this.isAdaptSubscriptionPlanWhatsApp(this.props)) {
      return;
    }

    this.setState({ whatsAppShow: true });
  };

  render() {
    const { activeSendRequestModal, activeScheduleDemoModal, actionSurvey, width } = this.state;
    const { i18n } = this.props;

    const GET_SUPPORT_SECTIONS = [
      [
        {
          label: this.isAdaptSubscriptionPlanWhatsApp(this.props)
            ? 'AdminHTML.DashboardV3.GetSupport.section01_Card01_2'
            : 'AdminHTML.DashboardV3.GetSupport.section01_Card01',
          image: chatIcon,
          action: this.isAdaptSubscriptionPlanWhatsApp(this.props)
            ? this.openWhatsApp
            : this.openChatBox
        },
        {
          label: 'AdminHTML.DashboardV3.GetSupport.section01_Card02',
          image: emailIcon,
          action: () => this.toggleModal('activeSendRequestModal')
        },
        {
          label: 'AdminHTML.DashboardV3.GetSupport.section01_Card03',
          image: phoneIcon,
          action: () => this.toggleModal('activeScheduleDemoModal')
        }
      ],
      [
        {
          label: 'AdminHTML.DashboardV3.GetSupport.section02_Card01',
          image: questionIcon,
          action: () => this.openLink('HelpCenter')
        },
        {
          label: 'AdminHTML.DashboardV3.GetSupport.section02_Card02',
          image: watchIcon,
          action: () => this.openLink('PlaylistTut')
        },
        {
          label: 'AdminHTML.DashboardV3.GetSupport.section02_Card03',
          image: noteIcon,
          action: () => this.openLink('Blogs')
        }
      ],
      [
        {
          label: 'AdminHTML.DashboardV3.GetSupport.section03_Card',
          image: sendIcon,
          action: () => this.toggleModal('actionSurvey')
        }
      ]
    ];

    return (
      <div className="BC-Support-Feature__DashboardV3">
        <Card sectioned>
          {['section01', 'section02', 'section03'].map((section, index) => (
            <div className="section" key={`section-${index}`}>
              <Heading>
                {i18n.translate(`AdminHTML.DashboardV3.GetSupport.${section}_Title`)}
              </Heading>
              <p>{i18n.translate(`AdminHTML.DashboardV3.GetSupport.${section}_Content`)}</p>
              <div className={`action-list ${width < 1000 ? 'action-list-mini' : ''}`}>
                {GET_SUPPORT_SECTIONS[index].map((card, cardIndex) => (
                  <div onClick={card.action} className="card-action" key={`card-${cardIndex}`}>
                    <img width={20} height={20} src={card.image} alt={card.label} />
                    <TextStyle variation="strong">{i18n.translate(card.label)}</TextStyle>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Card>
        <SendSupportTicket
          activeSendRequestModal={activeSendRequestModal}
          handleChangeModal={() => this.toggleModal('activeSendRequestModal')}
        />
        <ScheduleDemo
          activeScheduleDemoModal={activeScheduleDemoModal}
          handleChangeModal={() => this.toggleModal('activeScheduleDemoModal')}
        />

        {/* <WhatsAppPopup active={whatsAppShow} handleCancel={this.handleCancelWhatsApp} /> */}
        <TypeFormEmbededFeedback type="feedback" conditionShowStateChange={actionSurvey} />
      </div>
    );
  }
  openChatBox = () => {
    showChatbox();
  };

  toggleModal = fieldName => {
    const { themes, getThemes } = this.props;
    if (fieldName === 'activeSendRequestModal' && themes.length === 0) getThemes();

    const value = this.state[fieldName];
    if (fieldName === 'activeSendRequestModal' && value) {
      // Trigger when turn off the modal
      this.handleShowWhatsApp();
    }
    this.setState({ [fieldName]: !value });
  };

  // Open new page with link
  openLink = (id: string) => {
    const url = URL[id];
    window.open(url, '_blank');
  };

  handleCancelWhatsApp = (isConnectWhatsApp = false) => {
    // Connect when user click "Text us now" on whatsapp popup
    if (this.whatsAppAppearanceCount < WHATSAPP_MAXIMUM_APPEARANCE) {
      if (isConnectWhatsApp) {
        this.whatsAppAppearanceCount = WHATSAPP_MAXIMUM_APPEARANCE;
      } else {
        this.whatsAppAppearanceCount = Number(this.whatsAppAppearanceCount) + 1;
      }
      try {
        localStorage.setItem(
          `${this.shop}-${KEY_COUNT_CLOSE_WHATSAPP}`,
          String(this.whatsAppAppearanceCount)
        );
      } catch (error) {}
    }

    if (!this.isAlreadySetToday && !this.autoShowWhatsApp) {
      this.isAlreadySetToday = true;
      setWithExpiry(`${this.shop}-${KEY_ONCE_PER_DAY}`, true, TIME_LOCK_SEND_SUPPORT_TICKET);
    } else if (!this.isAlreadyAccessToday && this.autoShowWhatsApp) {
      this.isAlreadyAccessToday = true;
      this.autoShowWhatsApp = false;
      setWithExpiry(`${this.shop}-${KEY_THREE_TIMES_PER_DAY}`, true, TIME_LOCK_AUTO_SHOW);
    }

    this.setState({
      whatsAppShow: false
    });
  };
}

const mapStateToProps = (state, props) => {
  const tenantAppGroups = _.get(state.oauth, 'tenantAppGroups', []);
  let isAdaptMinTotalValue = false;
  for (let group of tenantAppGroups) {
    const totalValueOfPlans = group.reduce((cur, next) => {
      return cur + _.get(next, 'plantDetail.price', 0);
    }, 0);
    if (totalValueOfPlans >= MIN_TOTAL_VALUE_OF_PLANS) {
      isAdaptMinTotalValue = true;
      break;
    }
  }

  const packagePlans = _.get(state.subscriptionV3, 'packagePlans', {});
  const chargeInfo = _.get(state.subscriptionV3, 'invoice.chargeInfo', {});
  const originalPlan = _.get(chargeInfo, 'name', '');
  const currentTotalProducts = _.get(state.oauth, 'tenantApp.appParams.currentTotalProducts', 0);
  // Get correct plan
  const correctPlan = !_.isEmpty(chargeInfo)
    ? getCorrectPlan(chargeInfo, packagePlans, currentTotalProducts)
    : {};

  return {
    tenantAppGroups,
    isAdaptMinTotalValue,
    themes: _.get(state.themeSetup, 'themes', []),
    correctPlan,
    chargeInfo,
    originalPlan
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getThemes: () => dispatch(themeSetupAction.getThemes(ownProps.i18n, true)),
  getTenantAppGroups: () => dispatch(OauthAction.getTenantAppGroups({}, ownProps.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(GetSupport));
